/**
 * DetailHeroScene — Unified hero component for content detail pages
 *
 * Features:
 * - Full-viewport background image
 * - Vertical/horizontal gradient overlays
 * - Title, subtitle, metadata (year, country, duration, age)
 * - IMDB rating and like percentage
 * - Genre chips with navigation
 * - Glassmorphism action buttons (Play, Preview, Like, Bookmark)
 * - Optional video logo
 *
 * Ported from App-WebTV/src/components/common/DetailHeroScene.jsx
 * Adapted: removed spatial navigation, styled-components → inline styles,
 * useNavigate → Next.js useRouter, getOptimizedImageForType → web defaults.
 */
"use client";

import React from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import OptimizedImage, { OptimizedBackgroundImage } from "./OptimizedImage";
import {
  GlassmorphismButton,
  GlassmorphismIconButton,
} from "./GlassmorphismButton";
import {
  getOptimizedImageForType,
  tagsGuide,
  TURKISH_NATIVE_BADGE_SRC,
} from "@/utils/cms.utils";
import { getAgeRangeLabel } from "@/helpers/data.utils";
import { getContentAgeRangeValue } from "@/helpers/ageRange.utils";
import useWindowSize from "@/helpers/hooks/useWindowSize";
import { isComingSoon } from "@/utils/contentStatus";

// ============================================================================
// STYLES
// ============================================================================

const styles = {
  container: {
    position: "relative",
    width: "100%",
    minHeight: "95svh",
    overflow: "hidden",
    direction: "rtl",
  },

  verticalOverlay: {
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(to top, #121212 0%, rgba(18,18,18,0.6) 20%, rgba(18,18,18,0.2) 50%, rgba(18,18,18,0.05) 80%, transparent 100%)",
    zIndex: 3,
    pointerEvents: "none",
  },

  horizontalOverlay: {
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(to left, transparent 0%, rgba(18,18,18,0.15) 40%, rgba(18,18,18,0.4) 70%, rgba(18,18,18,0.7) 100%)",
    zIndex: 4,
    pointerEvents: "none",
  },

  contentWrapper: {},

  contentInner: {
    flex: "1 1 auto",
  },

  title: {
    fontSize: 42,
    fontWeight: 800,
    color: "#FFFFFF",
    margin: "0 0 4px 0",
    fontFamily: "'AnjomanMax', sans-serif",
    textShadow: "0 2px 8px rgba(0,0,0,0.8)",
    lineHeight: 1.2,
  },

  subtitle: {
    fontSize: 28,
    fontWeight: 400,
    color: "rgba(255,255,255,0.6)",
    margin: "0 0 16px 0",
    fontFamily: "'Inter', sans-serif",
  },

  metadataRow: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    marginBottom: 12,
    flexWrap: "wrap",
  },

  metadataItem: {
    fontSize: 15,
    color: "rgba(255,255,255,0.7)",
    fontFamily: "'AnjomanMax', sans-serif",
  },

  ratingRow: {
    display: "flex",
    alignItems: "center",
    gap: 20,
    marginBottom: 16,
  },

  ratingItem: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },

  ratingText: {
    fontSize: 16,
    fontWeight: 600,
    color: "#FFFFFF",
    fontFamily: "'Inter', sans-serif",
  },

  genreChipsContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 16,
  },

  genreChip: {
    display: "inline-block",
    padding: "6px 16px",
    fontSize: 14,
    fontWeight: 500,
    color: "rgba(255,255,255,0.85)",
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.15)",
    borderRadius: 20,
    cursor: "pointer",
    transition: "all 0.15s ease",
    textDecoration: "none",
    fontFamily: "'AnjomanMax', sans-serif",
  },

  description: {
    fontSize: 15,
    lineHeight: 1.7,
    color: "rgba(255,255,255,0.65)",
    margin: "0 0 20px 0",
    fontFamily: "'AnjomanMax', sans-serif",
    maxWidth: 600,
  },

  actionButtonsRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
  },

  iconActionsContainer: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    marginRight: 12,
  },

  videoLogoContainer: {
    position: "absolute",
    bottom: 48,
    left: 48,
    zIndex: 6,
  },

  tagBadge: {
    width: 160,
    height: "auto",
    marginBottom: 18,
    display: "block",
    borderRadius: 9999,
  },

  ageBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "2px 8px",
    fontSize: 12,
    fontWeight: 700,
    borderRadius: 4,
    color: "#FFF",
  },
};

// ============================================================================
// GENRE CHIP
// ============================================================================

const GenreChipLink = ({ genre, kind = "movie" }) => {
  return (
    <Link
      href={`/genre/${genre.slug}?kind=${kind}`}
      style={styles.genreChip}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "rgba(255,255,255,0.2)";
        e.currentTarget.style.borderColor = "rgba(255,255,255,0.4)";
        e.currentTarget.style.color = "#FFFFFF";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "rgba(255,255,255,0.08)";
        e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)";
        e.currentTarget.style.color = "rgba(255,255,255,0.85)";
      }}
    >
      {genre.name}
    </Link>
  );
};

// ============================================================================
// AGE RANGE BADGE
// ============================================================================

const AgeBadge = ({ ageRange }) => {
  if (!ageRange) return null;

  const colors = {
    G: "#4CAF50",
    PG: "#FF9800",
    "PG-13": "#FF5722",
    R: "#F44336",
    "NC-17": "#D32F2F",
  };

  return (
    <span
      style={{
        ...styles.ageBadge,
        background: colors[ageRange] || "#757575",
      }}
    >
      {ageRange}+
    </span>
  );
};

const formatCountryList = (country) => {
  if (Array.isArray(country)) {
    return country.filter(Boolean).join();
  }

  return country || "";
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const DetailHeroScene = ({
  content,
  onPlay,
  onTrailer,
  onBookmark,
  onLike,
  onDislike,
  playLabel = "تماشا کن",
  isBookmarked = false,
  isLiked = false,
  isDisliked = false,
  isPlayLoading = false,
  isSeries = false,
  renderButtons,
}) => {
  const windowSize = useWindowSize();

  // Extract content data
  const title = content?.title || "";
  const titleEn = content?.titleEN || content?.titleEn || "";
  const description = content?.description || "";
  const year = content?.year;
  const country = formatCountryList(content?.country);
  const time = content?.time;
  const imdbRate = content?.imdb_rate || content?.imdbRate;
  const ageRange = getContentAgeRangeValue(content);
  const genres = content?.genreInfo || [];
  const tags = content?.tags || [];
  const trailer = content?.trailer;
  const isLive =
    content?.isLive === true ||
    content?.isLive === 1 ||
    content?.isLive === "1";

  const hasTag = (tagCode) =>
    tags.some((tagLabel) => tagsGuide[tagLabel] === tagCode);
  const heroTag = hasTag("TURKISH_REPORT")
    ? { src: "/tags/gozaresh-large.svg", alt: "گزارش ترکی" }
    : hasTag("TURKISH_NATIVE")
    ? {
        src: '/tags/turkish-alt-full.svg',
        alt: "ترکی دیلینده",
      }
    : null;

  // Get images (web optimized)
  const backgroundImageData = getOptimizedImageForType(
    "horizontalCover",
    content?.contentPhotos,
    windowSize,
    { raw: true }
  );
  const videoLogoData = getOptimizedImageForType(
    "videoLogo",
    content?.contentPhotos,
    "web",
    { raw: true }
  );

  // Truncate description
  const truncatedDescription =
    description.length > 200
      ? description.substring(0, 200) + "..."
      : description;

  // Like percentage from IMDB or explicit rate
  const likePercentage =
    content?.rate || (imdbRate ? Math.round(imdbRate * 10) : null);

  return (
    <div
      className={`detail-hero-scene ${isLive ? "detail-hero-live" : ""} ${
        isSeries ? "detail-hero-series" : ""
      }`}
      style={styles.container}
    >
      {/* Background */}
      <OptimizedBackgroundImage
        src={backgroundImageData.src}
        alt={title}
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          left: -1,
          right: -1,
          width: "calc(100% + 2px)",
          height: "100%",
          zIndex: 1,
        }}
      />

      {/* Gradient overlays */}
      <div style={styles.verticalOverlay} />
      <div style={styles.horizontalOverlay} />

      <div className="detail-hero-content-shell relative md:container mx-auto w-full z-50 min-h-[95svh] flex items-end box-border px-5 pt-24 pb-8 md:pb-12 2xl:px-0 xlg:max-w-[1920px]">
        {/* Video logo (bottom left for RTL layout) */}
        {videoLogoData?.src && (
          <div
            style={styles.videoLogoContainer}
            className="content-detail-video-logo hidden md:block"
          >
            <OptimizedImage
              src={videoLogoData.src}
              alt={title}
              style={{ width: 350, height: "auto" }}
            />
          </div>
        )}

        <div className="detail-hero-content" style={styles.contentInner}>
          {/* Content tag badge */}
          {isComingSoon(content?.status) ? (
            <span className="mb-4 inline-flex rounded-full border border-red-400/60 bg-red-950/90 px-4 py-2 text-sm font-semibold text-white">
              بزودی
            </span>
          ) : heroTag && (
            <img
              src={heroTag.src}
              alt={heroTag.alt}
              className="content-detail-tag-badge"
              style={styles.tagBadge}
            />
          )}

          {/* Title */}
          <h1 className="content-detail-title" style={styles.title}>
            {title}
          </h1>

          {/* English title */}
          {titleEn && (
            <p className="content-detail-title-en" style={styles.subtitle}>
              {titleEn}
            </p>
          )}

          {/* Metadata */}
          <div
            className="content-detail-metadata-row"
            style={styles.metadataRow}
          >
            {(year || country) && (
              <span
                className="content-detail-metadata-item"
                style={styles.metadataItem}
              >
                {year || ""}
                {year && country ? " | " : ""}
                {country}
              </span>
            )}
            {time && (
              <span
                className="content-detail-metadata-item"
                style={styles.metadataItem}
              >
                {time} دقیقه
              </span>
            )}
            {ageRange && getAgeRangeLabel(ageRange)}
          </div>

          {/* Ratings */}
          <div className="content-detail-rating-row" style={styles.ratingRow}>
            {imdbRate && (
              <div style={styles.ratingItem}>
                <img
                  src="/imdb-icon.svg"
                  alt="IMDB"
                  style={{ width: 40, height: "auto" }}
                />
                <span style={styles.ratingText}>{imdbRate}</span>
              </div>
            )}
            {likePercentage && (
              <div style={styles.ratingItem}>
                <img
                  src="/heart-icon.svg"
                  alt="liked"
                  style={{ width: 20, height: "auto" }}
                />
                <span style={styles.ratingText}>{likePercentage}%</span>
              </div>
            )}
          </div>

          {/* Genre chips */}
          {genres.length > 0 && (
            <div
              className="content-detail-genres"
              style={styles.genreChipsContainer}
            >
              {genres.map((genre, index) => (
                <GenreChipLink
                  key={genre.slug + index}
                  genre={genre}
                  kind={content.contentType?.kind}
                />
              ))}
            </div>
          )}

          {/* Description */}
          {truncatedDescription && (
            <p
              className="content-detail-description"
              style={styles.description}
            >
              {truncatedDescription}
            </p>
          )}

          {/* Action buttons */}
          <div
            className="content-detail-actions-row"
            style={styles.actionButtonsRow}
          >
            {renderButtons ? (
              renderButtons()
            ) : (
              <>
                {/* Play button */}
                {!isSeries && onPlay && (
                  <GlassmorphismButton
                    label={playLabel}
                    icon="▶"
                    variant="primary"
                    className="content-detail-play-button"
                    loading={isPlayLoading}
                    onClick={onPlay}
                  />
                )}

                {/* Preview/Trailer */}
                {trailer && onTrailer && (
                  <GlassmorphismButton
                    label="پیش نمایش"
                    variant="secondary"
                    className="content-detail-secondary-action content-detail-preview-action"
                    onClick={onTrailer}
                  />
                )}

                {/* Icon actions */}
                {(onLike || onDislike || onBookmark) && (
                  <div
                    className="content-detail-content-actions"
                    style={styles.iconActionsContainer}
                  >
                    {onLike && !isSeries && (
                      <GlassmorphismIconButton
                        icon={
                          <img
                            src={
                              isLiked
                                ? "/svg/thumbs-up-filled.svg"
                                : "/svg/thumbs-up.svg"
                            }
                            alt="Like"
                            width="22"
                            height="22"
                            style={{ filter: "brightness(0) invert(1)" }}
                          />
                        }
                        onClick={onLike}
                        title="پسندیدم"
                      />
                    )}
                    {onDislike && !isSeries && (
                      <GlassmorphismIconButton
                        icon={
                          <img
                            src={
                              isDisliked
                                ? "/svg/thumbs-down-filled.svg"
                                : "/svg/thumbs-down.svg"
                            }
                            alt="Dislike"
                            width="22"
                            height="22"
                            style={{ filter: "brightness(0) invert(1)" }}
                          />
                        }
                        onClick={onDislike}
                        title="نپسندیدم"
                      />
                    )}
                    {onBookmark && (
                      <GlassmorphismIconButton
                        icon={
                          <img
                            src={
                              isBookmarked
                                ? "/svg/bookmark-filled.svg"
                                : "/svg/bookmark-empty.svg"
                            }
                            alt="Bookmark"
                            width="22"
                            height="22"
                            style={{ filter: "brightness(0) invert(1)" }}
                          />
                        }
                        onClick={onBookmark}
                        title="نشان‌گذاری"
                      />
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailHeroScene;
